/**
 * Session Metrics Chart
 *
 * Time-series visualization of session metrics
 * showing trends over the selected time period
 */

import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import type { SessionMetric } from "@/hooks/useAnalytics";

interface SessionMetricsChartProps {
  data: SessionMetric[];
  isLoading?: boolean;
}

export function SessionMetricsChart({
  data,
  isLoading,
}: SessionMetricsChartProps) {
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload?.length) {
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border-2 border-slate-200">
          <p className="font-bold text-slate-900 mb-3">{formatDate(label)}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 mb-1">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <p className="text-sm text-slate-600">
                {entry.name}:{" "}
                <span className="font-semibold" style={{ color: entry.color }}>
                  {entry.value.toLocaleString()}
                </span>
              </p>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px] bg-slate-50 rounded-lg">
        <div className="animate-pulse text-slate-400">
          Loading chart data...
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
        <p className="text-lg font-bold text-slate-700 mb-2">No Session Data</p>
        <p className="text-sm text-slate-600">
          Chart will appear once session data is collected
        </p>
      </div>
    );
  }

  // Format data for chart
  const chartData = data.map((item) => ({
    ...item,
    date: formatDate(item.date),
    fullDate: item.date,
  }));

  return (
    <div className="space-y-6">
      {/* Session Trends */}
      <div className="bg-white p-6 rounded-lg">
        <h3 className="text-lg font-bold text-slate-900 mb-4">
          Session Trends
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#B87333" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#B87333" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="sessions"
              stroke="#B87333"
              fillOpacity={1}
              fill="url(#colorSessions)"
              name="Sessions"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Page Views Trends */}
      <div className="bg-white p-6 rounded-lg">
        <h3 className="text-lg font-bold text-slate-900 mb-4">
          Page Views Over Time
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="page_views"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: "#3b82f6", r: 4 }}
              name="Page Views"
            />
            <Line
              type="monotone"
              dataKey="sessions"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ fill: "#10b981", r: 4 }}
              name="Sessions"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Average Session Duration */}
      <div className="bg-white p-6 rounded-lg">
        <h3 className="text-lg font-bold text-slate-900 mb-4">
          Average Session Duration
        </h3>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorDuration" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis
              label={{ value: "Seconds", angle: -90, position: "insideLeft" }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="avg_duration_seconds"
              stroke="#8b5cf6"
              fillOpacity={1}
              fill="url(#colorDuration)"
              name="Avg Duration (s)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
